import confetti from "canvas-confetti";

export interface ConfettiOptions {
  particleCount?: number;
  spread?: number;
  origin?: { x: number; y: number };
  colors?: string[];
  shapes?: ("square" | "circle")[];
  ticks?: number;
  gravity?: number;
  drift?: number;
  startVelocity?: number;
  decay?: number;
  scalar?: number;
  mode?: "confetti" | "fireworks" | "snow" | "stars" | "hearts" | "burst";
}

export class ConfettiService {
  private static instance: ConfettiService;
  private isEnabled: boolean = true;

  private constructor() {}

  public static getInstance(): ConfettiService {
    if (!ConfettiService.instance) {
      ConfettiService.instance = new ConfettiService();
    }
    return ConfettiService.instance;
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  public isConfettiEnabled(): boolean {
    return this.isEnabled;
  }

  /**
   * Trigger a basic confetti burst
   */
  public triggerConfetti(options?: ConfettiOptions): void {
    if (!this.isEnabled) return;

    const mode = options?.mode || "confetti";

    switch (mode) {
      case "fireworks":
        this.triggerFireworks(options);
        break;
      case "snow":
        this.triggerSnow(options);
        break;
      case "stars":
        this.triggerStars(options);
        break;
      case "hearts":
        this.triggerHearts(options);
        break;
      case "burst":
        this.triggerBurst(options);
        break;
      default:
        this.triggerBasicConfetti(options);
    }
  }

  /**
   * Trigger basic confetti
   */
  private triggerBasicConfetti(options?: ConfettiOptions): void {
    const defaultOptions: ConfettiOptions = {
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 0.5 },
      colors: ["#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57", "#ff9ff3", "#54a0ff"],
      shapes: ["square", "circle"],
      ticks: 200,
      gravity: 0.8,
      drift: 0,
      startVelocity: 30,
      decay: 0.94,
      scalar: 1,
    };

    const finalOptions = { ...defaultOptions, ...options };

    confetti({
      particleCount: finalOptions.particleCount,
      spread: finalOptions.spread,
      origin: finalOptions.origin,
      colors: finalOptions.colors,
      shapes: finalOptions.shapes,
      ticks: finalOptions.ticks,
      gravity: finalOptions.gravity,
      drift: finalOptions.drift,
      startVelocity: finalOptions.startVelocity,
      decay: finalOptions.decay,
      scalar: finalOptions.scalar,
    });
  }

  /**
   * Trigger fireworks effect
   */
  private triggerFireworks(options?: ConfettiOptions): void {
    const colors = options?.colors || [
      "#ff6b6b",
      "#4ecdc4",
      "#45b7d1",
      "#96ceb4",
      "#feca57",
      "#ff9ff3",
      "#54a0ff",
    ];
    const origin = options?.origin || { x: 0.5, y: 0.5 };

    // Multiple bursts for fireworks effect
    const count = 3;
    const defaults = {
      startVelocity: 30,
      spread: 70,
      ticks: 200,
      origin: origin,
    };

    function randomInRange(min: number, max: number) {
      return Math.random() * (max - min) + min;
    }

    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        confetti({
          ...defaults,
          particleCount: 50,
          colors: colors,
          shapes: ["square", "circle"],
          gravity: 0.8,
          drift: randomInRange(-0.5, 0.5),
          startVelocity: randomInRange(25, 35),
          decay: 0.94,
          scalar: randomInRange(0.8, 1.2),
        });
      }, i * 200);
    }
  }

  /**
   * Trigger snow effect
   */
  private triggerSnow(options?: ConfettiOptions): void {
    const colors = options?.colors || ["#ffffff", "#f0f8ff", "#e6f3ff", "#ddeeff"];
    const origin = options?.origin || { x: 0.5, y: 0 };

    confetti({
      particleCount: 150,
      spread: 100,
      origin: origin,
      colors: colors,
      shapes: ["circle"],
      ticks: 300,
      gravity: 0.3,
      drift: 0.1,
      startVelocity: 20,
      decay: 0.98,
      scalar: 0.8,
    });
  }

  /**
   * Trigger stars effect
   */
  private triggerStars(options?: ConfettiOptions): void {
    const colors = options?.colors || ["#ffd700", "#ffed4e", "#fff176", "#ffeb3b", "#ffc107"];
    const origin = options?.origin || { x: 0.5, y: 0.5 };

    // Create star shapes using multiple bursts
    const count = 5;
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        confetti({
          particleCount: 30,
          spread: 60,
          origin: origin,
          colors: colors,
          shapes: ["square"],
          ticks: 250,
          gravity: 0.6,
          drift: 0,
          startVelocity: 25,
          decay: 0.92,
          scalar: 1.2,
        });
      }, i * 100);
    }
  }

  /**
   * Trigger hearts effect
   */
  private triggerHearts(options?: ConfettiOptions): void {
    const colors = options?.colors || ["#ff69b4", "#ff1493", "#ffc0cb", "#ffb6c1", "#ffa0d0"];
    const origin = options?.origin || { x: 0.5, y: 0.5 };

    // Create heart-like effect with multiple bursts
    const count = 4;
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        confetti({
          particleCount: 40,
          spread: 45,
          origin: origin,
          colors: colors,
          shapes: ["circle"],
          ticks: 200,
          gravity: 0.5,
          drift: 0,
          startVelocity: 20,
          decay: 0.95,
          scalar: 1.0,
        });
      }, i * 150);
    }
  }

  /**
   * Trigger burst effect
   */
  private triggerBurst(options?: ConfettiOptions): void {
    const colors = options?.colors || [
      "#ff6b6b",
      "#4ecdc4",
      "#45b7d1",
      "#96ceb4",
      "#feca57",
      "#ff9ff3",
      "#54a0ff",
    ];
    const origin = options?.origin || { x: 0.5, y: 0.5 };

    // Multiple rapid bursts
    const count = 6;
    for (let i = 0; i < count; i++) {
      setTimeout(() => {
        confetti({
          particleCount: 25,
          spread: 90,
          origin: origin,
          colors: colors,
          shapes: ["square", "circle"],
          ticks: 150,
          gravity: 1.0,
          drift: 0,
          startVelocity: 40,
          decay: 0.9,
          scalar: 0.8,
        });
      }, i * 50);
    }
  }

  /**
   * Trigger confetti from a specific position (useful for hotspot clicks)
   */
  public triggerConfettiFromPosition(x: number, y: number, options?: ConfettiOptions): void {
    if (!this.isEnabled) return;

    // Convert screen coordinates to normalized coordinates (0-1)
    const normalizedX = x / window.innerWidth;
    const normalizedY = y / window.innerHeight;

    this.triggerConfetti({
      ...options,
      origin: { x: normalizedX, y: normalizedY },
    });
  }

  /**
   * Trigger a celebration confetti (multiple bursts)
   */
  public triggerCelebrationConfetti(): void {
    if (!this.isEnabled) return;

    // First burst
    this.triggerConfetti({
      particleCount: 100,
      spread: 70,
      origin: { x: 0.5, y: 0.5 },
    });

    // Second burst after a short delay
    setTimeout(() => {
      this.triggerConfetti({
        particleCount: 50,
        spread: 100,
        origin: { x: 0.3, y: 0.5 },
      });
    }, 150);

    // Third burst
    setTimeout(() => {
      this.triggerConfetti({
        particleCount: 50,
        spread: 100,
        origin: { x: 0.7, y: 0.5 },
      });
    }, 300);
  }

  /**
   * Trigger confetti with custom colors
   */
  public triggerCustomConfetti(colors: string[], options?: ConfettiOptions): void {
    if (!this.isEnabled) return;

    this.triggerConfetti({
      ...options,
      colors,
    });
  }

  /**
   * Clear all confetti particles
   */
  public clearConfetti(): void {
    confetti.reset();
  }
}

// Export a singleton instance
export const confettiService = ConfettiService.getInstance();
